import { appConfig as commonAppConfig } from 'fym-common/src/config/appConfig';
import { Environment } from 'fym-common/src/config/types';
import { Config } from './types';

/** The build passes the shared names (`craco.config.js` PASSTHROUGH_ENV). A missing key sends nothing. */
/**
 * The product events (Hernas Events API). The keys are PUBLIC client keys (contract v1 section 3):
 * the production key in production, the dev key (events tagged `dev`) on staging. A local build has
 * no key and prints the events. `HERNAS_EVENTS_URL` / `HERNAS_EVENTS_PUBLIC_KEY` of the build win.
 */
const HERNAS_EVENTS_PUBLIC_KEY_PRODUCTION = 'pk_music24_lJRpul51zBKmKNLBlQdA0BKhKpHgvZ2j';
const HERNAS_EVENTS_PUBLIC_KEY_DEV = 'pk_music24_x8tc3DwzaiuZnrzHtj1wK3TpwLnr3FUw';

const hernasEventsConfig = (defaultKey: string) => {
  const url = process.env.HERNAS_EVENTS_URL;
  const key = process.env.HERNAS_EVENTS_PUBLIC_KEY;
  return {
    hernasEventsURL: (url !== undefined && url !== '' ? url : 'https://events.hernas.com').replace(/\/+$/, ''),
    hernasEventsPublicKey: key !== undefined && key !== '' ? key : defaultKey,
  };
};

export const appConfig: Config = (platform, target, environment) => {
  console.log(`Preparing music24 config... platform: ${platform}, target: ${target}, env: ${environment}`);
  // The target is a constant of this app, not an env var, so the shared config is
  // built with it instead of being read from the CONFIG_TARGET-driven singleton.
  const commonConfig = commonAppConfig(platform, target, environment);
  const sentryDNS = 'https://f2d7ec5dfea915e49c5dafd88d990106@o4512050880774144.ingest.de.sentry.io/4512050909151312';
  if (environment === Environment.production) {
    return {
      ...commonConfig,
      platform,
      target,
      ...hernasEventsConfig(HERNAS_EVENTS_PUBLIC_KEY_PRODUCTION),
      gTagID: 'G-QSGVRGFN28',
      fbPixelID: '1741236473527955',
      // Production only. The web app has its own Clarity project — `ynaki0k0v5` is
      // music24.com's (music24-website); the mobile apps share a third. Staging, beta and local
      // send nothing — see the music24 skill's Analytics note.
      clarityID: 'ynapdwkubx',
      nativeAppScheme: 'music24',
      music24ApiPublicURL: 'https://api.music24.com/',
      sentryDNS,
      // Cloudflare Turnstile widget "Music24 Production" (account Hernas, domain music24.com, managed
      // mode). music24-api checks its tokens with MUSIC24_TURNSTILE_SECRET_KEY.
      turnstileSiteKey: '0x4AAAAAAB0K5xO6OxWFYAL8',
      loginMailSender: 'support@music24.com',
      stripeBillingUrl: 'https://billing.stripe.com/p/login/dRm7sLbyx76o24cdSw6sw00',
      plausible: { siteId: 'app.music24.com', url: 'https://plausible.freeyourmusic.com/' },
      googleClientId: '905057411890-tlj6uph0o5579r7j3kite4oblrghfmun.apps.googleusercontent.com',
      appleServicesId: 'com.music24',
      appleRedirectUri: 'https://music24.com/app/callback/apple',
    };
  }
  if (environment === Environment.staging) {
    return {
      ...commonConfig,
      platform,
      target,
      ...hernasEventsConfig(HERNAS_EVENTS_PUBLIC_KEY_DEV),
      gTagID: 'G-T6RE3BR9L8',
      fbPixelID: '',
      clarityID: '',
      // The RC native builds read staging.
      nativeAppScheme: 'music24rc',
      music24ApiPublicURL: 'https://api.musicpawa.com/',
      sentryDNS,
      // Cloudflare Turnstile widget "Music24 Staging" (account Hernas). Its domain list must hold
      // musicpawa.com (site.musicpawa.com), or the check fails in the browser.
      turnstileSiteKey: '0x4AAAAAAB0K5NBkKbYHusG3',
      // Staging sends from mg.musicpawa.com (SMARTLINKS_FROM_DOMAIN=musicpawa.com).
      loginMailSender: 'support@musicpawa.com',
      stripeBillingUrl: 'https://billing.stripe.com/p/login/test_fZufZh9jx6IQ6bK7fWeEo00',
      plausible: null,
      googleClientId: '369214998007-fhekp3a3530l8d8eapbaflas57e2tnlc.apps.googleusercontent.com',
      appleServicesId: 'com.music24.rc',
      appleRedirectUri: 'https://site.musicpawa.com/app/callback/apple',
    };
  }
  return {
    ...commonConfig,
    platform,
    target,
    ...hernasEventsConfig(''),
    gTagID: '',
    fbPixelID: '',
    clarityID: '',
    nativeAppScheme: 'music24local',
    // Locally the shared config already points the absolute API URL at music24-api.
    music24ApiPublicURL: commonConfig.fymApiURL(false),
    sentryDNS: '',
    // Cloudflare's invisible test key: it always passes and never shows a check
    // (developers.cloudflare.com/turnstile/troubleshooting/testing). music24-api/.env.ci has the
    // matching test secret. '3x00000000000000000000FF' forces the interactive check; '2x00000000000000000000BB'
    // always fails. An empty key turns the check off in the browser.
    turnstileSiteKey: '1x00000000000000000000BB',
    // music24-api/.env.local: SMARTLINKS_FROM_DOMAIN=hern.as (the mail lands in MailCrab).
    loginMailSender: 'support@hern.as',
    stripeBillingUrl: 'https://billing.stripe.com/p/login/test_fZufZh9jx6IQ6bK7fWeEo00',
    plausible: null,
    googleClientId: '369214998007-fhekp3a3530l8d8eapbaflas57e2tnlc.apps.googleusercontent.com',
    // Local Apple SSO tested via a Tailscale Funnel HTTPS domain (Apple rejects localhost).
    // Reuses the staging Services ID (com.music24.rc) — add this funnel domain + return URL to it.
    appleServicesId: 'com.music24.rc',
    appleRedirectUri: 'https://bmbp.han-nessie.ts.net/app/callback/apple',
  };
};
